import React, { useState, useEffect, useRef, useCallback } from "react";
import "./HomePage.css";
import "./LoadingPage.css";
import { useNavigate } from "react-router-dom";
import { GoTrash } from "react-icons/go";
import { LuPlus } from "react-icons/lu";
import { useUser } from "@clerk/clerk-react";
import { initialDropdownState } from "./DropdownProvider";
import DropdownProvider from "./DropdownProvider";
import DropdownPage from "./DropdownPage";

function buildFreshPages(wineCodes) {
  return wineCodes.map((code, i) => ({
    id: (Date.now() + i).toString(),
    data: { ...initialDropdownState, winecode: code, textAreaContentOeil: code },
    name: code,
    winecode: code,
  }));
}

// Returns 'empty' | 'started' | 'complete' based on how much of a page is filled
function getPageStatus(data) {
  if (!data) return "empty";
  const checks = [
    !!data.selectedColor,
    Array.isArray(data.selectedOption2) && data.selectedOption2.length > 0,
    Number(data.personalRating) > 0,
    !!data.textAreaComment,
  ];
  const filled = checks.filter(Boolean).length;
  if (filled === 0) return "empty";
  if (filled >= 3) return "complete";
  return "started";
}

const HomePage = ({ authCode }) => {
  const { user } = useUser();
  const [pages, setPages] = useState([]);
  const [activePageId, setActivePageId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const navigate = useNavigate();
  const toastTimer = useRef(null);

  const showToast = useCallback((message, type = "success") => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);

  useEffect(() => {
    if (!authCode) return;

    const existing = JSON.parse(localStorage.getItem("pages")) || [];
    if (existing.length > 0) {
      setPages(existing);
      setActivePageId(existing[0].id);
      return;
    }

    fetch(`/api/workshop-info?code=${encodeURIComponent(authCode)}`)
      .then((r) => r.json())
      .then(({ wineCodes }) => {
        const fresh = buildFreshPages(wineCodes);
        localStorage.setItem("pages", JSON.stringify(fresh));
        setPages(fresh);
        setActivePageId(fresh[0].id);
      })
      .catch(() => showToast("Code atelier introuvable — vérifiez votre code.", "error"));
  }, [authCode, showToast]);

  const createNewPage = () => {
    const newPage = {
      id: Date.now().toString(),
      data: { ...initialDropdownState },
      name: `Page ${pages.length + 1}`,
    };
    const updated = [...pages, newPage];
    localStorage.setItem("pages", JSON.stringify(updated));
    setPages(updated);
    setActivePageId(newPage.id);
  };

  const handleDelete = (id) => {
    setSelectedPage(id);
    setShowModal(true);
  };

  const confirmDelete = () => {
    const updated = pages.filter((p) => p.id !== selectedPage);
    localStorage.setItem("pages", JSON.stringify(updated));
    setPages(updated);
    if (selectedPage === activePageId) {
      setActivePageId(updated.length > 0 ? updated[0].id : null);
    }
    setShowModal(false);
    setSelectedPage(null);
  };

  const handleSendAllPages = async () => {
    const email = user?.primaryEmailAddress?.emailAddress;
    const name = user?.fullName || user?.firstName || '';
    setIsLoading(true);
    const savedPages = JSON.parse(localStorage.getItem("pages")) || [];

    try {
      const response = await fetch("/api/save-pages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          name,
          pages: savedPages.map(({ id, data }) => ({ id, data })),
        }),
      });

      if (response.ok) {
        localStorage.removeItem("pages");
        sessionStorage.clear();
        setPages([]);
        setActivePageId(null);
        navigate("/Success");
      } else {
        showToast("Échec de l'envoi — veuillez réessayer.", "error");
      }
    } catch {
      showToast("Erreur réseau — vérifiez votre connexion.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const activePage = pages.find((p) => p.id === activePageId);

  return (
    <div className="homepage-container">
      <div className="tab-header">
        {pages.map((page) => {
          const status = getPageStatus(page.data);
          return (
            <button
              key={page.id}
              className={`tab-button ${page.id === activePageId ? "active" : ""}`}
              onClick={() => setActivePageId(page.id)}
            >
              {page.name || `Page ${page.id}`}
              <span className={`tab-status tab-status--${status}`} title={status} />
              <GoTrash
                onClick={(e) => { e.stopPropagation(); handleDelete(page.id); }}
                className="delete-icon"
              />
            </button>
          );
        })}
        <button className="tab-button add-tab" onClick={createNewPage}>
          <LuPlus />
        </button>
      </div>

      <div className="tab-content">
        {activePage ? (
          <DropdownProvider pageId={activePage.id}>
            <DropdownPage page={activePage} key={activePage.id} />
          </DropdownProvider>
        ) : (
          <p>Créez une fiche pour commencer.</p>
        )}
        <div className="btn-group">
          <div className="btn-rect" onClick={handleSendAllPages}>
            <p>Valider mes fiches</p>
          </div>
        </div>
      </div>

      <div className="footer-homePage" />

      {showModal && (
        <>
          <div className="modal-overlay" onClick={() => setShowModal(false)} />
          <div className="modal">
            <p>Voulez-vous vraiment supprimer cette fiche ?</p>
            <button className="confirm" onClick={confirmDelete}>Oui</button>
            <button className="cancel" onClick={() => setShowModal(false)}>Annuler</button>
          </div>
        </>
      )}

      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
        </div>
      )}

      {toast && (
        <div className={`toast toast--${toast.type}`}>{toast.message}</div>
      )}
    </div>
  );
};

export default HomePage;
